(() => {
  const money = (cents, format) => {
    const amount = (Number(cents || 0) / 100).toLocaleString(document.documentElement.lang || 'nl-NL', {
      style: 'currency',
      currency: format || 'EUR'
    });
    return amount;
  };

  class NomoProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.product-form');
      this.variantsNode = this.querySelector('[data-product-variants]');
      if (!this.variantsNode) return;

      try {
        const inventory = JSON.parse(this.querySelector('[data-product-inventory]')?.textContent || '{}');
        this.variants = (JSON.parse(this.variantsNode.textContent) || []).map(variant => ({...variant,...(inventory[String(variant.id)] || {})}));
      } catch (_) {
        return;
      }

      this.currency = this.dataset.currency || 'EUR';
      this.giftCopy = JSON.parse(this.querySelector('[data-gift-copy]')?.textContent || '{}');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.quantityInput = this.form?.querySelector('input[name="quantity"]');
      this.buyButton = this.form?.querySelector('[data-buy-button]');
      this.buyLabel = this.buyButton?.querySelector('[data-atc-label]') || this.buyButton;
      this.errorNode = this.querySelector('[data-product-error]');
      this.setupGallery();
      this.updateGalleryForVariant(this.dataset.selectedSize || 'small', this.dataset.selectedColor || 'black', 'nomo-gallery-video');
      if (!this.form) return;

      this.picks = [...this.querySelectorAll('[data-pick]')];
      this.picks.forEach(pick => this.syncPick(pick));
      this.querySelector('[data-picks]')?.addEventListener('change', event => {
        const pick = event.target.closest('[data-pick]');
        if (!pick) return;
        this.syncPick(pick);
        this.refreshBundleAvailability();
        const variant = this.pickVariant(pick);
        if (variant) this.updateGalleryForVariant(variant.options[0], variant.options[1], variant.featured_media?.id ? String(variant.featured_media.id) : null);
      });
      this.querySelectorAll('[data-quantity-choice]').forEach((input) => {
        input.addEventListener('change', () => this.updateBundleState(input.value));
      });
      this.updateBundleState(this.querySelector('[data-quantity-choice]:checked')?.value || '1');
      this.saleTicker = window.setInterval(() => {
        const ended = Date.now() >= Date.parse(this.dataset.saleEnd || '');
        if (ended !== this.saleEnded) { this.saleEnded = ended; this.refreshBundleAvailability(); }
      }, 1000);
      this.addEventListener('nomo:upsell-change', () => this.refreshBundleAvailability());
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.shippingTicker = window.setInterval(() => this.updateShippingStatus(this.bundleAvailable), 60000);
    }

    disconnectedCallback() {
      if (this.shippingTicker) window.clearInterval(this.shippingTicker);
      if (this.saleTicker) window.clearInterval(this.saleTicker);
    }

    pickValue(pick, kind) {
      return pick.querySelector(`[data-pick-${kind}]:checked`)?.value;
    }

    findVariant(size, colour) {
      return this.variants.find(variant => variant.options[0] === size && variant.options[1] === colour);
    }

    pickVariant(pick) {
      return this.findVariant(this.pickValue(pick, 'size'), this.pickValue(pick, 'color'));
    }

    syncPick(pick) {
      const size = this.pickValue(pick, 'size') || 'Small';
      const colours = [...pick.querySelectorAll('[data-pick-color]')];
      colours.forEach(input => {
        const variant = this.findVariant(size, input.value);
        input.disabled = !variant?.available;
        input.closest('label')?.setAttribute('title', variant?.available ? input.value : `${input.value} · niet leverbaar in ${size}`);
      });
      const checked = colours.find(input => input.checked);
      if (!checked || checked.disabled) {
        const first = colours.find(input => !input.disabled);
        if (first) first.checked = true;
      }
      const variant = this.pickVariant(pick);
      const label = pick.querySelector('[data-pick-selection]');
      if (label && variant) label.textContent = variant.options.join(' · ') + (this.limitedCount([variant]) ? ' · +€5' : '');
    }

    setupGallery() {
      const section = this.closest('.product-hero');
      if (!section || this.galleryInitialized) return;
      this.galleryInitialized = true;
      section.querySelectorAll('[data-gallery-thumb]').forEach(button => {
        button.addEventListener('click', () => this.selectMedia(button.dataset.mediaId));
      });
      const move = direction => {
        const items = this.galleryItems || [];
        if (!items.length) return;
        const requestedIndex = items.findIndex(item => item.dataset.mediaId === this.requestedMediaId);
        const index = requestedIndex >= 0 ? requestedIndex : items.findIndex(item => !item.hidden);
        const next = items[(index + direction + items.length) % items.length];
        this.selectMedia(next.dataset.mediaId);
      };
      section.querySelector('[data-gallery-prev]')?.addEventListener('click', () => move(-1));
      section.querySelector('[data-gallery-next]')?.addEventListener('click', () => move(1));
      const stage = section.querySelector('.gallery-stage');
      stage?.addEventListener('keydown', event => {
        if (event.target !== stage || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      let touchStart = null;
      stage?.addEventListener('touchstart', event => {
        touchStart = event.touches.length === 1 && !event.target.closest('iframe,button,a') ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null;
      }, {passive:true});
      stage?.addEventListener('touchend', event => {
        if (!touchStart || !event.changedTouches[0]) return;
        const dx = event.changedTouches[0].clientX - touchStart.x;
        const dy = event.changedTouches[0].clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) move(dx < 0 ? 1 : -1);
      }, {passive:true});
      stage?.addEventListener('touchcancel', () => { touchStart = null; }, {passive:true});
      let dragStart = null;
      stage?.addEventListener('dragstart', event => event.preventDefault());
      stage?.addEventListener('pointerdown', event => {
        dragStart = event.pointerType === 'mouse' && event.button === 0 && !event.target.closest('iframe,button,a') ? event.clientX : null;
      });
      stage?.addEventListener('pointerup', event => {
        if (dragStart === null) return;
        const dx = event.clientX - dragStart;
        dragStart = null;
        if (Math.abs(dx) > 50) move(dx < 0 ? 1 : -1);
      });
      stage?.addEventListener('pointerleave', () => { dragStart = null; });
      const dialog = section.querySelector('[data-photo-dialog]');
      const zoom = section.querySelector('[data-gallery-zoom]');
      zoom?.addEventListener('click', () => {
        const source = this.galleryItems?.find(item => !item.hidden)?.querySelector('img');
        const target = dialog?.querySelector('[data-photo-image]');
        if (!source || !target || typeof dialog.showModal !== 'function') return;
        target.src = source.currentSrc || source.src;
        target.alt = source.alt;
        dialog.showModal();
      });
      dialog?.querySelector('[data-photo-close]')?.addEventListener('click', () => dialog.close());
      dialog?.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
      dialog?.addEventListener('close', () => zoom?.focus({preventScroll:true}));
      section.querySelector('[data-gallery-video-open]')?.addEventListener('click', async event => {
        const button = event.currentTarget;
        if (button.dataset.mode === 'product') {
          const firstPhoto = (this.galleryItems || []).find(item => item.dataset.mediaId !== 'nomo-gallery-video');
          if (firstPhoto) this.selectMedia(firstPhoto.dataset.mediaId);
          return;
        }
        await this.selectMedia('nomo-gallery-video');
        const video = section.querySelector('.gallery-item--video video');
        if (video) {
          video.muted = true;
          video.play().catch(() => {});
        }
      });
    }

    async selectMedia(mediaId) {
      const section = this.closest('.product-hero');
      if (!section) return;
      const available = this.galleryItems || [];
      const active = available.find(item => item.dataset.mediaId === mediaId) || available[0];
      const request = Symbol('gallery-selection');
      this.mediaRequest = request;
      this.requestedMediaId = active?.dataset.mediaId;
      const incomingImage = active?.querySelector('img');
      const stage = section.querySelector('.gallery-stage');
      stage?.setAttribute('aria-busy', 'true');
      if (incomingImage && !incomingImage.complete) {
        incomingImage.loading = 'eager';
        try { await incomingImage.decode(); } catch (_) { /* Let the image element handle load failure. */ }
      }
      if (this.mediaRequest !== request || !this.isConnected) return;
      stage?.setAttribute('aria-busy', 'false');
      const changed = active?.hidden;
      this.galleryAnimation?.cancel();
      section.querySelectorAll('[data-gallery-item]').forEach(item => {
        const selected = item === active;
        item.hidden = !selected;
        item.setAttribute('aria-hidden', String(!selected));
        if (!selected) item.querySelectorAll('video').forEach(video => video.pause());
      });
      section.querySelectorAll('[data-gallery-thumb]').forEach(button => {
        const selected = !button.hidden && button.dataset.mediaId === active?.dataset.mediaId;
        button.setAttribute('aria-current', String(selected));
        if (selected) {
          const track = button.parentElement;
          const left = button.offsetLeft - track.offsetLeft;
          if (left < track.scrollLeft || left + button.offsetWidth > track.scrollLeft + track.clientWidth) {
            track.scrollTo({left:Math.max(0,left - (track.clientWidth - button.offsetWidth) / 2),behavior:'auto'});
          }
        }
      });
      const videoButton = section.querySelector('[data-gallery-video-open]');
      if (videoButton) {
        const onVideo = active?.dataset.mediaId === 'nomo-gallery-video';
        videoButton.dataset.mode = onVideo ? 'product' : 'video';
        videoButton.classList.toggle('is-product', onVideo);
        const label = videoButton.querySelector('[data-gallery-video-label]');
        if (label) label.textContent = onVideo ? videoButton.dataset.labelProduct : videoButton.dataset.labelVideo;
      }
      const activeVideo = active?.classList.contains('gallery-item--video') ? active.querySelector('video') : null;
      if (activeVideo && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        activeVideo.muted = true;
        activeVideo.play().catch(() => {});
      }
      const tools = section.querySelector('[data-gallery-tools]');
      if (tools) tools.hidden = !active;
      const count = section.querySelector('[data-gallery-count]');
      if (count) count.textContent = active ? (available.indexOf(active) + 1) + ' / ' + available.length : '';
      section.querySelectorAll('[data-gallery-prev],[data-gallery-next]').forEach(button => { button.disabled = available.length < 2; });
      const image = active?.querySelector('img');
      const zoom = section.querySelector('[data-gallery-zoom]');
      const dialog = section.querySelector('[data-photo-dialog]');
      if (zoom) zoom.hidden = !image || typeof dialog?.showModal !== 'function';
      const caption = section.querySelector('[data-gallery-caption]');
      if (caption) caption.textContent = image?.alt && !image.alt.startsWith('Maat:') ? image.alt : '';
      if (changed && active?.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.galleryAnimation = active.animate([{opacity:.78},{opacity:1}], {duration:300,easing:'cubic-bezier(.22,1,.36,1)'});
      }
      const nextImage = available[(available.indexOf(active) + 1) % available.length]?.querySelector('img');
      if (nextImage) nextImage.loading = 'eager';
    }

    updateGalleryForVariant(size, color, preferredMediaId = null) {
      const section = this.closest('.product-hero');
      if (!section) return;
      this.dataset.selectedSize = size.toLocaleLowerCase('nl-NL');
      this.dataset.selectedColor = color.toLocaleLowerCase('nl-NL');
      const matches = item => {
        const itemSize = item.dataset.gallerySize || '';
        const itemColor = item.dataset.galleryColor || '';
        return (!itemSize || itemSize === this.dataset.selectedSize) && (!itemColor || itemColor === this.dataset.selectedColor);
      };
      const allItems = [...section.querySelectorAll('[data-gallery-item]')];
      const matched = allItems.filter(matches);
      // Fixed story: video, the open → rolled → keychain steps, second video, then max. 3 product photos.
      const byId = id => matched.find(item => item.dataset.mediaId === id);
      const lead = byId('nomo-gallery-video');
      const stepItems = matched.filter(item => item.dataset.galleryStep).sort((a, b) => a.dataset.galleryStep - b.dataset.galleryStep);
      const second = byId('nomo-gallery-video-2');
      const sizes = matched.find(item => item.dataset.gallerySizes);
      const story = new Set([lead, ...stepItems, second, sizes].filter(Boolean));
      // Market lifestyle shot stays out of the slider.
      const isMarket = item => /-market|black-groceries/.test(item.querySelector('img')?.getAttribute('src') || '');
      // Two shots with the tote open close the slider.
      const openShots = [byId('nomo-black-lifestyle-03')].filter(Boolean);
      const openSrc = new Set(openShots.map(item => item.querySelector('img')?.getAttribute('src')));
      let rest = matched.filter(item => !story.has(item) && !isMarket(item) && !openShots.includes(item) && !openSrc.has(item.querySelector('img')?.getAttribute('src')));
      const preferred = rest.find(item => item.dataset.mediaId === preferredMediaId);
      if (preferred) rest = [preferred, ...rest.filter(item => item !== preferred)];
      // With the open → dicht steps in view, the size chart replaces the first packshot.
      else if (stepItems.length && sizes) rest = rest.slice(1);
      // Colours with their own matching photo set show exactly that set after the videos and size chart.
      const setItems = matched.filter(item => item.dataset.gallerySet).sort((a, b) => a.dataset.gallerySet - b.dataset.gallerySet);
      if (setItems.length) {
        this.galleryItems = [...story, ...setItems];
        if (preferredMediaId && preferredMediaId !== 'nomo-gallery-video') preferredMediaId = setItems[0].dataset.mediaId;
      } else {
        this.galleryItems = [...story, ...rest.slice(0, 2), ...openShots];
      }
      if (!this.galleryItems.length) this.galleryItems = allItems.slice(0,1);
      const thumbWrap = section.querySelector('.gallery-thumbs');
      if (thumbWrap) this.galleryItems.forEach(item => {
        const thumb = thumbWrap.querySelector(`[data-gallery-thumb][data-media-id="${CSS.escape(item.dataset.mediaId)}"]`);
        if (thumb) thumbWrap.appendChild(thumb);
      });
      const ids = new Set(this.galleryItems.map(item => item.dataset.mediaId));
      section.querySelectorAll('[data-gallery-thumb]').forEach(button => {
        button.hidden = !ids.has(button.dataset.mediaId) || !matches(button);
      });
      const active = this.galleryItems.find(item => item.dataset.mediaId === preferredMediaId) || this.galleryItems[0];
      this.selectMedia(active?.dataset.mediaId);
    }

    selectedQuantity() {
      return Number(this.querySelector('[data-quantity-choice]:checked')?.value || 1);
    }

    updateBundleState(quantity) {
      const count = Number(quantity) || 1;
      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.querySelector('input')?.value === String(count));
      });
      this.picks.forEach((pick, index) => {
        pick.hidden = index >= count;
        const name = pick.querySelector('[data-pick-name]');
        if (name) name.textContent = count === 1 ? 'Jouw tas' : `Tas ${index + 1}`;
      });
      if (this.quantityInput) this.quantityInput.value = '1';
      this.refreshBundleAvailability();
    }

    bundleItems(quantity = this.selectedQuantity()) {
      return this.picks.slice(0, quantity).map(pick => this.pickVariant(pick));
    }

    limitedCount(items) {
      return items.filter(variant => /polka-dot/i.test(variant?.options?.[1] || '')).length;
    }

    priceSetting(name, fallback) {
      const value = Number(this.dataset[name]);
      return Number.isFinite(value) && value > 0 ? value : fallback;
    }

    // Van-prijs: som van de compare-at-prijzen van de gekozen varianten.
    compareTotal(items) {
      return items.reduce((sum, variant) => sum + Number(variant?.compare_at_price || variant?.price || 0), 0);
    }

    // Echte prijs: som van de variantprijzen min de automatische bundelkorting in Shopify,
    // zodat pagina en checkout altijd hetzelfde bedrag tonen.
    giftTotal(items) {
      if (![1, 2, 4].includes(items.length)) throw new Error('Unsupported bundle quantity');
      const sum = items.reduce((total, variant) => total + Number(variant?.price || 0), 0);
      const discount = items.length >= 4 ? this.priceSetting('discountFour', 5996) : items.length >= 2 ? this.priceSetting('discountTwo', 1998) : 0;
      return Math.max(0, sum - discount);
    }

    refreshBundleAvailability() {
      if (!this.buyButton) return;
      const items = this.bundleItems();
      if (this.variantInput && items[0]) this.variantInput.value = items[0].id;
      const counts = new Map();
      items.forEach(variant => {
        if (variant) counts.set(String(variant.id), (counts.get(String(variant.id)) || 0) + 1);
      });
      const available = items.every(variant => variant?.available && (
        !variant.inventory_management ||
        variant.inventory_policy === 'continue' ||
        (Number.isFinite(Number(variant.inventory_quantity)) && Number(variant.inventory_quantity) >= counts.get(String(variant.id)))
      ));
      const ready = items.length === 1 || this.dataset.checkoutReady === 'true';
      this.buyButton.disabled = !available || !ready;
      this.bundleAvailable = available && ready;
      this.updateShippingStatus(this.bundleAvailable);

      const short = cents => money(cents, this.currency).replace(',00', '');
      [1, 2, 4].forEach(count => {
        const bag = this.bundleItems(count);
        const price = this.giftTotal(bag);
        const was = this.compareTotal(bag);
        const totalNode = this.querySelector('[data-bundle-price="' + count + '"]');
        const wasNode = this.querySelector('[data-bundle-was="' + count + '"]');
        const unitNode = this.querySelector('[data-bundle-unit="' + count + '"]');
        const badgeNode = this.querySelector('[data-bundle-badge="' + count + '"]');
        if (totalNode) totalNode.textContent = short(price);
        if (wasNode) wasNode.textContent = was > price ? short(was) : '';
        if (unitNode) unitNode.textContent = count === 1 ? '' : short(price / count) + ' per tas';
        if (badgeNode) {
          const percent = Math.round((1 - price / was) * 100);
          badgeNode.textContent = count === 1 ? short(was - price) + ' korting' : count === 2 ? percent + '% korting' : 'Beste value';
          badgeNode.hidden = was <= price;
        }
      });

      const upsell = this.querySelector('nomo-keyring-upsell')?.selection;
      const upsellPrice = Number(upsell?.price || 0);
      const total = this.giftTotal(items) + upsellPrice;
      const was = this.compareTotal(items) + upsellPrice;
      this.querySelectorAll('[data-product-price]').forEach(node => { node.textContent = money(total, this.currency); });
      if (this.lastTotal !== undefined && this.lastTotal !== total) {
        this.querySelectorAll('[data-product-price]').forEach(node => { node.classList.remove('is-bump'); void node.offsetWidth; node.classList.add('is-bump'); });
      }
      this.lastTotal = total;
      const wasNode = this.querySelector('[data-price-was]');
      const saveNode = this.querySelector('[data-price-save]');
      if (wasNode) { wasNode.hidden = was <= total; wasNode.textContent = money(was, this.currency); }
      if (saveNode) { saveNode.hidden = was <= total; saveNode.textContent = 'Bespaar ' + money(was - total, this.currency); }
      const unit = this.querySelector('[data-price-unit]');
      if (unit) unit.textContent = (items.length > 1 ? `${money(this.giftTotal(items) / items.length, this.currency)} per tas · ` : '') + (upsell ? `incl. ${upsell.label} · ` : '') + 'gratis verzending';
      const saveCount = this.querySelector('[data-save-count]');
      if (saveCount) saveCount.textContent = String(items.length * 52);

      const section = this.closest('.product-hero');
      const stickyTotal = section?.querySelector('[data-sticky-total]');
      const stickyName = section?.querySelector('[data-sticky-name]');
      if (stickyTotal) stickyTotal.textContent = money(total, this.currency);
      if (stickyName) stickyName.textContent = `${items.length === 1 ? '1 tas' : items.length + ' tassen'} · ${items.map(v => v?.options[1]).filter(Boolean).join(', ')}`;

      this.buyLabel.textContent = !available ? this.buyButton.dataset.soldText : !ready ? this.giftCopy.unready : `${this.buyButton.dataset.availableText} · ${money(total, this.currency)}`;
    }

    updateShippingStatus(available) {
      const status = this.querySelector('[data-shipping-status]');
      const label = status?.querySelector('[data-ship-label]');
      if (!label) return;
      status.classList.toggle('is-off', !available);
      if (!available) {
        label.textContent = 'Deze keuze is momenteel niet beschikbaar';
        return;
      }
      const now = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Amsterdam', weekday: 'short', hour: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).filter((part) => ['weekday', 'hour'].includes(part.type)).map((part) => [part.type, part.value]));
      const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(now.weekday);
      const workday = day >= 1 && day <= 5;
      if (workday && Number(now.hour) < Number(status.dataset.cutoff || 17)) label.textContent = 'Vandaag verzonden';
      else if (day === 5 || day === 6) label.textContent = 'Maandag verzonden';
      else label.textContent = 'Morgen verzonden';
    }

    async submit(event) {
      event.preventDefault();
      this.refreshBundleAvailability();
      if (this.buyButton.disabled) return;
      this.buyButton.disabled = true;
      this.buyButton.classList.add('is-loading');
      this.errorNode.hidden = true;

      try {
        const counts = new Map();
        this.bundleItems().forEach((variant) => {
          if (!variant) throw new Error('Missing variant');
          counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
        });
        const upsell = this.querySelector('nomo-keyring-upsell')?.selection;
        if (upsell) counts.set(upsell.id, (counts.get(upsell.id) || 0) + 1);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [...counts].map(([id, quantity]) => ({ id, quantity })) })
        });
        if (!response.ok) throw new Error('Cart error');
        try { if (!localStorage.getItem('nomo-cart-reservation-until')) localStorage.setItem('nomo-cart-reservation-until', String(Date.now() + 600000)); } catch (_) {}
        const drawer = document.querySelector('nomo-cart');
        if (drawer?.open) { await drawer.open(); this.buyButton.classList.remove('is-loading'); this.refreshBundleAvailability(); }
        else window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (_) {
        this.errorNode.hidden = false;
        this.refreshBundleAvailability();
        this.buyButton.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('nomo-product')) customElements.define('nomo-product', NomoProduct);

  document.querySelectorAll('[data-select-color]').forEach((card) => {
    card.addEventListener('click', () => {
      const pick = document.querySelector('nomo-product [data-pick]');
      if (!pick) return;
      const size = [...pick.querySelectorAll('[data-pick-size]')].find((input) => input.value === card.dataset.selectSize);
      if (size) { size.checked = true; size.dispatchEvent(new Event('change', { bubbles: true })); }
      const colour = [...pick.querySelectorAll('[data-pick-color]')].find((input) => input.value === card.dataset.selectColor && !input.disabled);
      if (!colour) return;
      colour.checked = true;
      colour.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  document.querySelectorAll('[data-mobile-menu-button]').forEach(button => {
    const menu = document.getElementById(button.getAttribute('aria-controls'));
    if (!menu) return;
    const close = restoreFocus => {
      button.setAttribute('aria-expanded','false');
      menu.hidden = true;
      if (restoreFocus) button.focus({preventScroll:true});
    };
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded',String(open));
      menu.hidden = !open;
    });
    menu.addEventListener('click', event => { if (event.target.closest('a')) close(false); });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !menu.hidden) close(true);
    });
    document.addEventListener('click', event => {
      if (!menu.hidden && !menu.contains(event.target) && !button.contains(event.target)) close(false);
    });
    document.addEventListener('focusin', event => {
      if (!menu.hidden && !menu.contains(event.target) && !button.contains(event.target)) close(false);
    });
    window.matchMedia('(min-width: 991px)').addEventListener('change',event => {if(event.matches) close(false);});
  });

  const onScrollFrame = (() => {
    const callbacks = new Set();
    let queued = false;
    const run = () => { queued = false; callbacks.forEach(callback => callback()); };
    const queue = () => { if (!queued) { queued = true; window.requestAnimationFrame(run); } };
    window.addEventListener('scroll', queue, {passive:true});
    window.addEventListener('resize', queue, {passive:true});
    document.addEventListener('visibilitychange', queue);
    return callback => { callbacks.add(callback); callback(); };
  })();
  const inView = (el, share = 0) => {
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    const visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    return visible > 0 && visible >= r.height * share;
  };

  document.querySelectorAll('[data-sticky-buy]').forEach(sticky => {
    const section = sticky.closest('.product-hero');
    const target = section?.querySelector('[data-primary-buy]');
    if (!target) return;
    // Stays with the visitor all the way down (footer included); only hides while the real buy button is on screen.
    const state = {buy:false};
    const apply = () => {
      const visible = !state.buy;
      sticky.classList.toggle('is-visible', visible);
      sticky.inert = !visible;
      document.body.classList.toggle('has-nb-sticky', visible);
    };
    onScrollFrame(() => {
      state.buy = inView(target);
      apply();
    });
    const link = sticky.querySelector('a');
    link?.addEventListener('click', event => {
      event.preventDefault();
      const destination = section.querySelector(link.getAttribute('href')) || target.closest('form') || target;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      destination.scrollIntoView({behavior:reduceMotion ? 'auto' : 'smooth',block:'start'});
      (destination.querySelector?.('[data-quantity-choice]:checked') || target).focus({preventScroll:true});
    });
  });

  const isHorizontallyVisible = el => {
    const r = el.getBoundingClientRect();
    const rail = el.closest('.nb-clips__rail')?.getBoundingClientRect();
    const left = Math.max(r.left, rail ? rail.left : 0), right = Math.min(r.right, rail ? rail.right : window.innerWidth);
    return right - left >= r.width * .5;
  };
  // "Zie het in actie": muted autoplay while on screen, paused off screen, sound toggle per clip.
  document.querySelectorAll('[data-nb-clips]').forEach(wrap => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const videos = [...wrap.querySelectorAll('video')];
    videos.forEach(video => {
      video.muted = true;
      if (reduce) { video.controls = true; video.preload = 'metadata'; }
    });
    if (!reduce) {
      onScrollFrame(() => videos.forEach(video => {
        const show = inView(video, .55) && inView(video.closest('.nb-clips__rail') || video) && isHorizontallyVisible(video);
        if (show && video.paused) { video.preload = 'auto'; video.play().catch(() => {}); }
        else if (!show && !video.paused) video.pause();
      }));
      wrap.querySelector('.nb-clips__rail')?.addEventListener('scroll', () => window.dispatchEvent(new Event('scroll')), {passive:true});
    }
    wrap.addEventListener('click', event => {
      const button = event.target.closest('[data-clip-sound]');
      if (!button) return;
      const video = button.closest('.nb-clip')?.querySelector('video');
      if (!video) return;
      const on = video.muted;
      videos.forEach(other => { if (other !== video) { other.muted = true; other.closest('.nb-clip')?.querySelector('[data-clip-sound]')?.setAttribute('aria-pressed', 'false'); } });
      video.muted = !on;
      if (on) video.play().catch(() => {});
      button.setAttribute('aria-pressed', String(on));
      button.setAttribute('aria-label', on ? 'Geluid uit' : 'Geluid aan');
    });
  });



})();

(() => {
  const clocks = document.querySelectorAll('[data-midnight-clock]');
  if (!clocks.length) return;
  const update = () => {
    clocks.forEach(clock => {
      const now = new Date();
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Amsterdam', hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(now).map(part => [part.type, part.value]));
      const elapsed = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
      const left = 86399 - elapsed;
      clock.textContent = [Math.floor(left / 3600), Math.floor(left % 3600 / 60), left % 60].map(n => String(n).padStart(2,'0')).join(':');
      clock.removeAttribute('aria-label');
    });
  };
  update();
  window.setInterval(update,1000);
})();

// Scroll reveals live in assets/nomo-motion.js.

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  const init = root => {
    root.querySelectorAll('[data-motion-region]').forEach(region => {
      if (initialized.has(region)) return;
      initialized.add(region);
      const button = region.querySelector('[data-motion-toggle]');
      if (!button) return;
      button.hidden = false;
      button.addEventListener('click', () => {
        const paused = button.getAttribute('aria-pressed') !== 'true';
        region.classList.toggle('is-paused',paused);
        button.setAttribute('aria-pressed',String(paused));
        button.setAttribute('aria-label',paused ? button.dataset.playLabel : button.dataset.pauseLabel);
        button.querySelector('[data-motion-icon]').textContent = paused ? '▷' : 'Ⅱ';
      });
    });
    // Every horizontal rail swipes on touch and drags with the mouse; no arrow buttons needed.
    root.querySelectorAll('.nomo-community__track,[data-review-track],.product-hero .gallery-thumbs').forEach(track => {
      if (initialized.has(track)) return;
      initialized.add(track);
      const slider = track.closest('[data-community-gallery],[data-review-slider]') || track.parentElement;
      track.classList.add('nb-swipe');
      const prev = slider.querySelector('[data-prev],[data-review-prev]');
      const next = slider.querySelector('[data-next],[data-review-next]');
      const update = () => {
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      const move = direction => {
        const visible = [...track.children].filter(child => child.offsetWidth);
        const first = visible[0];
        const second = visible[1];
        const step = second ? second.offsetLeft - first.offsetLeft : track.clientWidth;
        track.scrollBy({left:direction * step,behavior:reduced.matches ? 'auto' : 'smooth'});
      };
      prev?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      let drag = null;
      track.addEventListener('dragstart', event => event.preventDefault());
      track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = {x:event.clientX,left:track.scrollLeft,moved:false};
      });
      track.addEventListener('pointermove', event => {
        if (!drag) return;
        const dx = event.clientX - drag.x;
        drag.dx = dx;
        if (!drag.moved && Math.abs(dx) > 4) { drag.moved = true; track.classList.add('is-dragging'); }
        track.scrollLeft = drag.left - dx;
      });
      const endDrag = () => {
        if (!drag) return;
        const moved = drag.moved;
        const {dx = 0, left} = drag;
        drag = null;
        track.classList.remove('is-dragging');
        // A short flick still moves one card in that direction instead of snapping back.
        if (moved && Math.abs(dx) > 30 && track.scrollWidth > track.clientWidth) {
          const visible = [...track.children].filter(child => child.offsetWidth);
        const first = visible[0];
          const second = visible[1];
          const step = second ? second.offsetLeft - first.offsetLeft : track.clientWidth;
          if (step <= 0) return;
          const pos = track.scrollLeft / step;
          const target = (dx < 0 ? Math.max(Math.ceil(pos), Math.round(left / step) + 1) : Math.min(Math.floor(pos), Math.round(left / step) - 1)) * step;
          track.scrollTo({left: Math.max(0, target), behavior: reduced.matches ? 'auto' : 'smooth'});
        }
        suppressClick = moved;
        window.setTimeout(() => { suppressClick = false; }, 0);
      };
      let suppressClick = false;
      track.addEventListener('click', event => { if (suppressClick) { event.preventDefault(); event.stopPropagation(); } }, true);
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointerleave', endDrag);
      track.addEventListener('scroll',update,{passive:true});
      track.addEventListener('keydown',event => {
        if (event.target !== track || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault();move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update);
        observer.observe(track);
        slider.addEventListener('shopify:section:unload', () => observer.disconnect(),{once:true});
      }
      update();
    });
    // Minimal chevrons next to every swipe rail (thumbnails excluded); they fade out at the ends.
    root.querySelectorAll('.nb-swipe').forEach(track => {
      if (track.closest('.gallery-thumbs') || track.classList.contains('gallery-thumbs') || track.dataset.nbChev) return;
      track.dataset.nbChev = '1';
      // Chevrons sit beside the rail itself, so wrap it when it shares a parent with a heading.
      let host = track.parentElement;
      if (host.children.length > 1) { host = document.createElement('div'); track.before(host); host.appendChild(track); }
      host.classList.add('nb-chev-host');
      const make = dir => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'nb-chev nb-chev--' + dir;
        button.setAttribute('aria-label', dir === 'prev' ? 'Vorige' : 'Volgende');
        button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' + (dir === 'prev' ? 'M15 4.5L7.5 12 15 19.5' : 'M9 4.5L16.5 12 9 19.5') + '" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        button.addEventListener('click', () => {
          const visible = [...track.children].filter(child => child.offsetWidth);
          const step = visible[1] ? visible[1].offsetLeft - visible[0].offsetLeft : track.clientWidth * 0.8;
          track.scrollBy({left: (dir === 'prev' ? -1 : 1) * step, behavior: reduced.matches ? 'auto' : 'smooth'});
        });
        host.appendChild(button);
        return button;
      };
      const prev = make('prev');
      const next = make('next');
      const sync = () => {
        const scrollable = track.scrollWidth > track.clientWidth + 4;
        prev.hidden = next.hidden = !scrollable;
        prev.classList.toggle('is-end', track.scrollLeft <= 4);
        next.classList.toggle('is-end', track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
      };
      track.addEventListener('scroll', sync, {passive: true});
      window.addEventListener('resize', sync);
      sync();
    });
    root.querySelectorAll('.nomo-action video').forEach(video => {
      if (initialized.has(video)) return;
      initialized.add(video);
      video.addEventListener('play', () => {
        document.querySelectorAll('.nomo-action video').forEach(other => {if(other !== video) other.pause();});
      });
    });
  };
  init(document);
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('[data-motion-region]').forEach(region => region.classList.toggle('is-background',document.hidden));
    if (document.hidden) document.querySelectorAll('.nomo-action video').forEach(video => video.pause());
  });
})();



// Header cart count: refresh when the page is shown again (e.g. back from checkout).
(() => {
  const refresh = () => {
    const nodes = document.querySelectorAll('[data-cart-count]');
    if (!nodes.length) return;
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {headers: {Accept: 'application/json'}})
      .then(response => response.json())
      .then(cart => nodes.forEach(node => {
        node.textContent = String(cart.item_count);
        node.hidden = cart.item_count === 0;
        node.closest('[data-cart-link]')?.setAttribute('aria-label', `Winkelmand, ${cart.item_count} ${cart.item_count === 1 ? 'artikel' : 'artikelen'}`);
      }))
      .catch(() => {});
  };
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
})();
